import { AttributionWindow } from "@redotech/redo-merchant-app-common/marketing/use-attribution";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { plainDateToCurrentInstant } from "@redotech/util/temporal";
import { createContext, ReactNode, useContext, useMemo, useState } from "react";
import { getDefaultMetricsDateRange } from "./utils/metrics-date-range";

export interface MetricsDateRangeValue {
  dateRange: [Temporal.PlainDate, Temporal.PlainDate];
  setDateRange: (
    range: [Temporal.PlainDate, Temporal.PlainDate] | undefined,
  ) => void;
}

const AutomationMetricsDateRangeContext = createContext<
  MetricsDateRangeValue | undefined
>(undefined);

export function AutomationMetricsDateRangeProvider({
  children,
  initialDateRange,
  onDateRangeChange,
}: {
  children: ReactNode | ReactNode[];
  initialDateRange?: [Temporal.PlainDate, Temporal.PlainDate];
  onDateRangeChange?: (range: [Temporal.PlainDate, Temporal.PlainDate]) => void;
}) {
  const [dateRange, setDateRangeState] = useState<
    [Temporal.PlainDate, Temporal.PlainDate]
  >(initialDateRange ?? getDefaultMetricsDateRange());

  const value = useMemo<MetricsDateRangeValue>(
    () => ({
      dateRange,
      setDateRange: (range) => {
        const next = range ?? getDefaultMetricsDateRange();
        setDateRangeState(next);
        onDateRangeChange?.(next);
      },
    }),
    [dateRange, onDateRangeChange],
  );

  return (
    <AutomationMetricsDateRangeContext.Provider value={value}>
      {children}
    </AutomationMetricsDateRangeContext.Provider>
  );
}

/**
 * Read the optional metrics date range scoped to the surrounding marketing
 * automation editor. Returns undefined when no provider is mounted (e.g. when
 * a metrics-aware node is rendered outside of the marketing flow editor).
 */
export function useOptionalAutomationMetricsDateRange():
  | MetricsDateRangeValue
  | undefined {
  return useContext(AutomationMetricsDateRangeContext);
}

/** The selected metrics range as an attribution window in the store's time zone.
 *  Undefined when no range is in context, so callers skip the query. */
export function useAutomationAttributionWindow():
  | AttributionWindow
  | undefined {
  const metricsDateRange = useOptionalAutomationMetricsDateRange();
  const team = useContext(TeamContext);
  const storeTimeZone = team?.ianaTimezone ?? Temporal.Now.timeZoneId();
  return useMemo(
    () =>
      metricsDateRange && {
        from: plainDateToCurrentInstant(
          metricsDateRange.dateRange[0],
          storeTimeZone,
        ),
        // The range's last day is inclusive; the window's end is not.
        to: plainDateToCurrentInstant(
          metricsDateRange.dateRange[1].add({ days: 1 }),
          storeTimeZone,
        ),
      },
    [metricsDateRange, storeTimeZone],
  );
}
